<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import fileIllustrationImg from '@tundr/theme-assets/images/file-illustration.png';
  import {
    FeedbackMessage,
    FeedbackMessageVariant,
  } from '@tundr/ds-components';
  import { useModal } from '../../composables/use-modal.ts';
  import ModalActions from '../ModalActions/ModalActions.vue';
  import ModalContent from '../ModalContent/ModalContent.vue';
  import ModalSkeleton from '../ModalSkeleton/ModalSkeleton.vue';
  import ModalTitle from '../ModalTitle/ModalTitle.vue';
  import ModalTitleImage from '../ModalTitleImage/ModalTitleImage.vue';
  import {
    exportModalContainerClass,
    exportModalContentClass,
    exportModalContentDisclaimerClass,
  } from './ExportModal.css.ts';

  const { t } = useLocale('export');

  const { closeModal } = useModal();

  const props = defineProps<{
    title?: string;
    message?: string;
    showPeriodDisclaimer?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    disableConfirm?: boolean;
    loadingConfirm?: boolean;
    disclaimerTitle?: string;
    disclaimerDescription?: string;
  }>();

  const emits = defineEmits<{
    confirm: [];
    cancel: [];
  }>();

  const onCancel = () => {
    emits('cancel');
    closeModal();
  };

  const onConfirm = () => {
    emits('confirm');
  };
</script>

<template>
  <ModalSkeleton :class="exportModalContainerClass">
    <template #title>
      <ModalTitle>
        <ModalTitleImage :src="fileIllustrationImg" />
        {{ props.title || t('modals.export.title') }}
      </ModalTitle>
    </template>
    <ModalContent :class="exportModalContentClass">
      {{ props.message || t('modals.export.subtitle') }}
      <br><br>
      <slot />
      <div
        v-if="props.showPeriodDisclaimer"
        :class="exportModalContentDisclaimerClass"
      >
        <FeedbackMessage
          :variant="FeedbackMessageVariant.INFO"
          distinct-id="export-modal-period-disclaimer"
          :message="props.disclaimerTitle || t('modals.export.disclaimer.title')"
          :description="
            props.disclaimerDescription ||
            t('modals.export.disclaimer.description')
          "
        />
      </div>
    </ModalContent>
    <template #actions>
      <ModalActions
        :confirm-label="props.confirmLabel || t('modals.export.actions.export')"
        :cancel-label="props.cancelLabel || t('common:actions.cancel')"
        :disable-confirm="props.disableConfirm"
        :loading-confirm="props.loadingConfirm"
        @confirm="onConfirm"
        @cancel="onCancel"
      />
    </template>
  </ModalSkeleton>
</template>
